import { v4 as uuidv4 } from 'uuid';

import {
  Counterparty,
  CounterpartyApproval,
  CounterpartyApprovalPatch,
  CounterpartyCreateUpdate,
  CounterpartySortColumns,
  CounterpartyType,
  ExpenseType
} from 'openapi';

import { getCounterpartyApprovalState } from 'utils/helpers/counterPartyApproval';
import { validateIban } from 'utils/helpers/invoiceHelpers';
import { CounterPartyFormValues } from 'utils/interfaces/CounterParty';

export const mapCounterPartyValues = (
  counterparty?: Counterparty
): CounterPartyFormValues => {
  if (!counterparty) {
    return {
      type: CounterpartyType.SUPPLIER,
      name: '',
      vatNumber: '',
      registrationNumber: '',
      expenseTypeIds: [],
      notes: '',
      ibans: []
    };
  }

  const expenseTypes: ExpenseType[] = counterparty.expenseTypes ?? [];

  return {
    type: counterparty.type ?? CounterpartyType.SUPPLIER,
    name: counterparty.name || '',
    vatNumber: counterparty.vatNumber || '',
    registrationNumber: counterparty.registrationNumber || '',
    expenseTypeIds: expenseTypes
      .map((expenseType) => expenseType.id)
      .filter((id): id is number => id !== undefined),
    notes: counterparty.notes || '',
    ibans: (counterparty.ibans || [])
      .slice()
      .sort((first, second) => (first.sortOrder || 0) - (second.sortOrder || 0))
      .map((iban) => ({
        id: iban.id,
        iban: iban.iban || '',
        isUsed: iban.isUsed,
        tmpId: uuidv4()
      }))
  };
};

export const getCounterPartyPayload = (
  values: CounterPartyFormValues
): CounterpartyCreateUpdate => {
  const name = values.name.trim();
  const registrationNumber = values.registrationNumber.trim();
  const isReimbursement = values.type === CounterpartyType.REIMBURSEMENT;
  const validIbans = values.ibans.filter(
    (iban) => !!iban.iban.trim() && validateIban(iban.iban.trim())
  );
  const { requiresExpenseType } = getCounterpartyApprovalState(values);
  const hasExpenseTypes =
    requiresExpenseType && values.expenseTypeIds.length > 0;

  return {
    name,
    type: values.type,
    ...(isReimbursement
      ? {}
      : {
          vatNumber: values.vatNumber.trim(),
          registrationNumber
        }),
    notes: values.notes.trim() || undefined,
    ...(hasExpenseTypes ? { expenseTypeIds: values.expenseTypeIds } : {}),
    ibans: validIbans.map((iban, index) => ({
      ...(iban.id ? { id: iban.id } : {}),
      iban: iban.iban.trim(),
      isSelectedForPayment: index === 0,
      isUsed: iban.isUsed || false,
      sortOrder: index
    }))
  };
};

export const getMappedCounterParties = (counterParties: Counterparty[]) =>
  counterParties.map((counterparty: Counterparty) => {
    return {
      ...counterparty,
      [CounterpartySortColumns.NAME]: counterparty.name,
      [CounterpartySortColumns.REGISTRATION_NUMBER]:
        counterparty.registrationNumber,
      [CounterpartySortColumns.VAT_NUMBER]: counterparty.vatNumber
    };
  });

export const mapCounterpartyApprovalValues = (
  values: CounterpartyApproval
): CounterpartyApprovalPatch & { name: string } => {
  return {
    id: values.id,
    name: values.name as string,
    approvalThreshold: values.approvalThreshold || 0,
    approverIds: values.approvers?.map((approver) => approver.id) || []
  };
};
